import { type FullAirHandlerUnit } from "@eisox/api/core-api";
import { ActionButton, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { AirHandlerUnitIcon, PencilIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "AirHandlerUnitDialog";

interface CommandFormValues {
  command: number | undefined;
  forcing: number | undefined;
}

interface AirHandlerUnitDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnit: FullAirHandlerUnit;
  children?: React.ReactNode;
}

const AirHandlerUnitDialog: React.FC<AirHandlerUnitDialogProps> = ({
  buildingId,
  airHandlerUnit,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const defaultValues: CommandFormValues = { command: airHandlerUnit.command, forcing: airHandlerUnit.forcing };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      await mutateAsync({ airHandlerUnitId: airHandlerUnit.id, body: dirty });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the unit's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} {...props}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={airHandlerUnit.name}
              subtitle={m.air_handler_unit_actuator_dialog_subtitle()}
              icon={<AirHandlerUnitIcon />}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <form.AppField name="command">
              {commandField => (
                <ActuatorDialog.Item label={m.control()}>
                  <ActuatorDialog.Command
                    commandMan={airHandlerUnit.commandMan}
                    value={commandField.state.value}
                    onValueChange={value => commandField.handleChange(value)}
                    disabled={disabled}
                  />
                  <form.AppField name="forcing">
                    {forcingField => (
                      <ActuatorDialog.Forcing
                        command={commandField.state.value}
                        commandMan={airHandlerUnit.commandMan}
                        commandType={airHandlerUnit.commandType}
                        value={forcingField.state.value}
                        onValueChange={value => forcingField.handleChange(value)}
                        disabled={disabled}
                      />
                    )}
                  </form.AppField>
                </ActuatorDialog.Item>
              )}
            </form.AppField>
            <ActuatorDialog.Item label={m.state()}>
              <ActuatorDialog.ActuatorState
                commandType={airHandlerUnit.commandType}
                state={airHandlerUnit.state}
                commandAutomate={airHandlerUnit.commandAutomate}
                commandMan={airHandlerUnit.commandMan}
                defect={!!airHandlerUnit.defect}
                missmatchDefect={!!airHandlerUnit.missmatchDefect}
              />
            </ActuatorDialog.Item>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

AirHandlerUnitDialog.displayName = NAME;

export { AirHandlerUnitDialog };
export type { AirHandlerUnitDialogProps };
